<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class=" text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Success Story Highlights</h3>
            <div class="text-[14px] leading-[22px] space-y-4 mb-6">
                <p>Describe stand-out success stories.</p>
                <p>Writing success stories for your case for support should include the following:</p>
                
                <ol class="list-decimal pl-6 space-y-3">
                    <li>Select a story that highlights a specific achievement, success or outcome that is relevant to your organization. The story should be compelling and demonstrate the impact of your organization's work.</li>
                    <li>Incorporate the challenge that was overcome, the actions taken to achieve success, and the impact of that success.</li>
                    <li>Frame the story in a storytelling format with a clear beginning, middle, and end.</li>
                    <li>Use data and visuals: Incorporate data and visuals to support the story and demonstrate the results achieved.</li>
                    <li>Acknowledge the role of donors in achieving the success, and emphasize the importance of continued support for your organization's work.</li>
                </ol>
            </div>
            
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Enter up to three (3) success stories that highlight your organization's achievements and impact on the community.</p>

            <div class="mt-6">
                <h4 class="text-[1rem] leading-[1.5rem] mb-[0.5rem] font-semibold">Success Stories <span class="text-[#6B7280]">(3 Stories Max)</span></h4>

                <!-- Success Stories Container -->
                <div class="mt-4">
                    <!-- Success Story Item -->
                    <div
                        v-for="(story, idx) in successStoryState.successStories"
                        :key="idx"
                        class="bg-[#F5F6FA] rounded-md overflow-hidden border border-[#00423D] mt-4"
                        :class="{'border-red-400 shakeX': !isSuccessStoriesValid.isValid}"
                    >
                        <!-- Story Header -->
                        <div class="py-2 px-4 flex items-center justify-between gap-4 flex-wrap">
                            <div class="flex flex-1 items-center gap-2 justify-between">
                                <span class="text-sm text-[#00423D]">Story {{ idx + 1 }}</span>
                                <button class="p-1" @click="onRemoveSuccessStory" :data-point-id="idx">
                                    <svg class="w-6 h-6 pointer-events-none" width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <mask id="mask0_1675_29390" style="mask-type: alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="49" height="49">
                                            <rect x="0.0527344" y="0.480957" width="48" height="48" fill="#D9D9D9"/>
                                        </mask>
                                        <g mask="url(#mask0_1675_29390)">
                                            <path d="M13.1027 42.481C12.2694 42.481 11.5611 42.1893 10.9777 41.606C10.3944 41.0226 10.1027 40.3143 10.1027 39.481V10.981H8.05273V7.98096H17.4527V6.48096H30.6527V7.98096H40.0527V10.981H38.0027V39.481C38.0027 40.281 37.7027 40.981 37.1027 41.581C36.5027 42.181 35.8027 42.481 35.0027 42.481H13.1027ZM35.0027 10.981H13.1027V39.481H35.0027V10.981ZM18.4027 35.181H21.4027V15.231H18.4027V35.181ZM26.7027 35.181H29.7027V15.231H26.7027V35.181Z" class="fill-[#6B7280]"/>
                                        </g>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Story Description -->
                        <div class="px-4 py-3 border-t border-t-gray-200 bg-white">
                            <textarea
                                class="w-full px-2 py-2 bg-transparent outline-none focus:outline-none transition-colors duration-200 resize-none"
                                rows="6"
                                placeholder="Write your success story here..."
                                @input="onSuccessStoryInput"
                                v-model="story.user_story"
                                :data-story-index="idx"
                            ></textarea>
                            <div class="mt-2 flex justify-between items-center">
                                <p :class="{ 'text-red-500': story.wordCount > 263 }" class="text-sm">
                                    {{ story.wordCount }} / 263 words
                                </p>
                                <p v-if="story.wordCount > 263" class="text-sm text-red-500">
                                    Please keep your story under 263 words
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Validation Message -->
                    <transition name="answer-err">
                        <p class="pl-2 mt-1.5 text-sm text-red-400" v-if="!isSuccessStoriesValid.isValid">
                            {{ isSuccessStoriesValid.issue === "empty"
                                ? "Please Add At Least One Success Story!"
                                : isSuccessStoriesValid.issue === "wordCount"
                                ? "One or more stories exceed the word limit of 263 words"
                                : "Please Add Your Success Story!"
                            }}
                        </p>
                    </transition>

                    <!-- Add Story Button -->
                    <button
                        v-if="successStoryState.successStories.length < 3"
                        class="flex items-center text-white bg-[#00423D] py-2 px-4 rounded-lg mt-4 hover:bg-[#009C87] transition-colors duration-200"
                        @click="onAddSuccessStory"
                    >
                        <svg class="h-6 w-6 pointer-events-none" width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <mask id="mask0_1409_29353" style="mask-type: alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="49" height="49">
                                <rect x="0.322266" y="0.00683594" width="48" height="48" fill="#D9D9D9"/>
                            </mask>
                            <g mask="url(#mask0_1409_29353)">
                                <path d="M24.3223 38.0068C23.8889 38.0068 23.5306 37.8652 23.2473 37.5818C22.9639 37.2985 22.8223 36.9402 22.8223 36.5068V25.5068H11.8223C11.3889 25.5068 11.0306 25.3652 10.7473 25.0818C10.4639 24.7985 10.3223 24.4402 10.3223 24.0068C10.3223 23.5735 10.4639 23.2152 10.7473 22.9318C11.0306 22.6485 11.3889 22.5068 11.8223 22.5068H22.8223V11.5068C22.8223 11.0735 22.9639 10.7152 23.2473 10.4318C23.5306 10.1485 23.8889 10.0068 24.3223 10.0068C24.7556 10.0068 25.1139 10.1485 25.3973 10.4318C25.6806 10.7152 25.8223 11.0735 25.8223 11.5068V22.5068H36.8223C37.2556 22.5068 37.6139 22.6485 37.8973 22.9318C38.1806 23.2152 38.3223 23.5735 38.3223 24.0068C38.3223 24.4402 38.1806 24.7985 37.8973 25.0818C37.6139 25.3652 37.2556 25.5068 36.8223 25.5068H25.8223V36.5068C25.8223 36.9402 25.6806 37.2985 25.3973 37.5818C25.1139 37.8652 24.7556 38.0068 24.3223 38.0068Z" class="fill-white"/>
                            </g>
                        </svg>
                        <span class="pointer-events-none ml-2">Add Success Story</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Confirmation Dialog -->
        <div v-if="conformationPopupIsOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 class="text-lg font-semibold mb-4">Confirm Deletion</h3>
                <p class="text-gray-600 mb-6">Are you sure you want to delete this success story?</p>
                <div class="flex justify-end gap-4">
                    <button
                        @click="onCancelRemove"
                        class="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                        Cancel
                    </button>
                    <button
                        @click="onConformRemove"
                        class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStepValidation } from '@/composables/useStepValidation';
import { useAuthStore } from "@/stores/auth";
import { useUserProjectsStore } from "@/stores/projects";
import global from "@/global";

// State management
const successStoryState = ref({
    successStories: []
});

const isSuccessStoriesValid = ref({
    isValid: true,
    issue: null
});

const isLoading = ref(false);
const bodyText = ref('');
const retrievedUserAnswer = ref(null);
const conformationPopupIsOpen = ref(false);
const conformDeletionState = ref(null);

// Store instances
const authStore = useAuthStore();
const projectsStore = useUserProjectsStore();

// Methods
const onAddSuccessStory = () => {
    if (successStoryState.value.successStories.length < 3) {
        successStoryState.value.successStories.push({
            user_story: '',
            wordCount: 0
        });
    }
};

const onRemoveSuccessStory = (event) => {
    const idx = event.currentTarget.dataset.pointId;
    conformationPopupIsOpen.value = true;
    conformDeletionState.value = idx;
};

const onCancelRemove = () => {
    conformationPopupIsOpen.value = false;
    conformDeletionState.value = null;
};

const onConformRemove = async () => {
    if (conformDeletionState.value === null) return;
    
    const idx = conformDeletionState.value;
    const storyToDelete = successStoryState.value.successStories[idx];
    
    try {
        // If the story has an ID, delete it from the API
        if (storyToDelete.id) {
            const res = await fetch(`${global.API_URL}/success-stories/${storyToDelete.id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${authStore.user.accessToken}`,
                },
            });
            
            if (!res.ok) {
                throw new Error(`HTTP error! Status: ${res.status}`);
            }
            
            const data = await res.json();
            if (!data.status) {
                throw new Error('Failed to delete story');
            }
        }
        
        // Remove from local state
        successStoryState.value.successStories.splice(idx, 1);
        
        // Notify the store that a question has been updated
        projectsStore.setQuestionUpdate();
    } catch (error) {
        console.error("Failed to delete success story:", error);
    } finally {
        conformationPopupIsOpen.value = false;
        conformDeletionState.value = null;
    }
};

const onSuccessStoryInput = (event) => {
    const idx = event.currentTarget.dataset.storyIndex;
    const text = event.target.value;
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    successStoryState.value.successStories[idx].wordCount = words.length;
    
    // Validate stories
    validateStories();
};

const validateStories = () => {
    // Only validate if there are stories
    if (successStoryState.value.successStories.length === 0) {
        return;
    }

    const hasInvalidStory = successStoryState.value.successStories.some(story => 
        story.wordCount > 263  // Only check word count during typing
    );

    isSuccessStoriesValid.value = {
        isValid: !hasInvalidStory,
        issue: hasInvalidStory ? 'wordCount' : null
    };
};

// API Integration
async function fetchSuccessStories() {
    try {
        const res = await fetch(`${global.API_URL}/success-stories`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });
        
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        
        const data = await res.json();
        if (data.stories && data.stories.length > 0) {
            // Transform the data to match our frontend structure
            const transformedStories = data.stories.map(story => ({
                id: story.id,
                user_story: story.user_story || '',
                order: story.order,
                wordCount: (story.user_story || '').split(/\s+/).filter(word => word !== "").length
            }));
            
            successStoryState.value.successStories = transformedStories;
            retrievedUserAnswer.value = transformedStories;
        }
    } catch (error) {
        console.error("Failed to fetch success stories:", error);
    }
}

async function saveSuccessStories() {
    try {
        const storiesToSave = successStoryState.value.successStories.map((story, index) => ({
            id: story.id,
            order: index + 1,
            user_story: story.user_story
        }));

        const res = await fetch(`${global.API_URL}/success-stories`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                stories: storiesToSave
            }),
        });
        
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        
        const data = await res.json();
        if (data.status) {
            // Notify the store that a question has been updated
            projectsStore.setQuestionUpdate();
            return true;
        }
        return false;
    } catch (error) {
        console.error("Failed to save success stories:", error);
        return false;
    }
}

// Use the validation composable with custom validation
useStepValidation({
    validateFn: async () => {
        // Full validation only happens when trying to proceed
        if (successStoryState.value.successStories.length === 0) {
            isSuccessStoriesValid.value = {
                isValid: false,
                issue: 'empty'
            };
            return false;
        }

        const hasEmptyStory = successStoryState.value.successStories.some(story => 
            !story.user_story?.trim()
        );

        const hasInvalidWordCount = successStoryState.value.successStories.some(story => 
            story.wordCount > 263
        );

        isSuccessStoriesValid.value = {
            isValid: !hasEmptyStory && !hasInvalidWordCount,
            issue: hasInvalidWordCount ? 'wordCount' : hasEmptyStory ? 'incomplete' : null
        };

        if (isSuccessStoriesValid.value.isValid) {
            return await saveSuccessStories();
        }
        return false;
    }
});

onMounted(async () => {
    isLoading.value = true;
    await fetchSuccessStories();
    isLoading.value = false;
});
</script>

<style scoped>
.answer-err-enter-active,
.answer-err-leave-active {
    transition: opacity 0.3s ease;
}

.answer-err-enter-from,
.answer-err-leave-to {
    opacity: 0;
}

.shakeX {
    animation: shakeX 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes shakeX {
    10%, 90% {
        transform: translate3d(-1px, 0, 0);
    }
    20%, 80% {
        transform: translate3d(2px, 0, 0);
    }
    30%, 50%, 70% {
        transform: translate3d(-4px, 0, 0);
    }
    40%, 60% {
        transform: translate3d(4px, 0, 0);
    }
}
</style>
